import React, { useEffect, useRef, useState } from 'react'
import { AiOutlineInfoCircle, AiOutlineLock, AiOutlineClose, AiOutlineInbox } from "react-icons/ai"
import '../../css/dashboard.css'

/**
 * Field Ops — shared look & helpers (interactive DEMO, 2026-10-29).
 *
 * Everything under pages/FieldOps runs on sample data held in memory (see store.js / demoData.js): no backend,
 * no Sage, no storage. Reloading the page restores the original data. To remove the whole feature: delete this
 * folder, the imports + routes in AppRouter.js and the "Field Ops" block in Menu.js.
 *
 * No optional chaining / nullish coalescing: this project's CRA version (react-scripts 2.1.3) can't compile them.
 */

export const COLORS = {
    navy: '#0F172A', blue: '#2563EB', green: '#10b981', amber: '#F59E0B', red: '#EF4444', purple: '#8b5cf6',
    slate: '#64748B', muted: '#94A3B8', line: '#E2E8F0', soft: '#F1F5F9', bg: '#F8FAFC',
}

/* ---------- buttons & inputs (solid colours: outline variants render invisible in this app's CSS) ---------- */
export const BTN_PRIMARY = { background: '#2563EB', border: 'none', borderRadius: 8, color: '#FFFFFF', fontWeight: 700, padding: '0.5rem 1.1rem', fontSize: '0.85rem', cursor: 'pointer' }
export const BTN_SUCCESS = { ...BTN_PRIMARY, background: '#10b981' }
export const BTN_DANGER = { ...BTN_PRIMARY, background: '#EF4444' }
export const BTN_SECONDARY = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, color: '#0F172A', fontWeight: 600, padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' }
export const BTN_SMALL = { ...BTN_SECONDARY, padding: '0.25rem 0.65rem', fontSize: '0.75rem' }
export const BTN_DISABLED = { opacity: 0.45, cursor: 'not-allowed' }
export const INPUT_STYLE = { border: '1px solid #E2E8F0', borderRadius: 8, padding: '0.4rem 0.7rem', fontSize: '0.85rem', color: '#0F172A', background: '#FFFFFF' }
export const SELECT_SMALL = { ...INPUT_STYLE, padding: '0.2rem 0.4rem', fontSize: '0.8rem' }

export const Btn = ({ kind, small, disabled, busy, onClick, children, title, style }) => {
    const base = kind === 'success' ? BTN_SUCCESS : kind === 'danger' ? BTN_DANGER : kind === 'secondary' ? (small ? BTN_SMALL : BTN_SECONDARY) : BTN_PRIMARY
    const sized = small && kind !== 'secondary' ? { ...base, padding: '0.25rem 0.7rem', fontSize: '0.75rem' } : base
    const off = disabled || busy
    return (
        <button type="button" title={title} disabled={off} onClick={off ? undefined : onClick} style={{ ...sized, ...(off ? BTN_DISABLED : {}), ...(style || {}) }}>
            {busy ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: 6 }}></span> : null}
            {children}
        </button>
    )
}

/* ---------- numbers ---------- */
export const money = (n) => '$' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
export const money2 = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
export const pct = (part, total) => (total > 0 ? Math.round((part / total) * 100) : 0)
export const lb = (n) => Number(n || 0).toLocaleString('en-US') + ' lb'

/* ---------- dates (ISO yyyy-mm-dd and yyyy-mm, local time) ---------- */
const two = (n) => (n < 10 ? '0' + n : '' + n)
export const isoDate = (d) => d.getFullYear() + '-' + two(d.getMonth() + 1) + '-' + two(d.getDate())
export const toDate = (iso) => { const p = iso.split('-'); return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2])) }
export const todayIso = () => isoDate(new Date())
export const addDays = (iso, n) => { const d = toDate(iso); d.setDate(d.getDate() + n); return isoDate(d) }
export const monthKey = (iso) => iso.slice(0, 7)
export const addMonths = (key, n) => { const p = key.split('-'); return isoDate(new Date(Number(p[0]), Number(p[1]) - 1 + n, 1)).slice(0, 7) }
export const daysInMonth = (key) => { const p = key.split('-'); return new Date(Number(p[0]), Number(p[1]), 0).getDate() }
export const monthLabelOf = (key) => { const p = key.split('-'); return new Date(Number(p[0]), Number(p[1]) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) }
export const weekday = (iso) => toDate(iso).getDay() // 0 Sun .. 6 Sat
export const startOfWeek = (iso) => { const w = weekday(iso); return addDays(iso, w === 0 ? -6 : 1 - w) } // Monday
export const fmtDate = (iso) => (iso ? toDate(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—')
export const fmtDateLong = (iso) => toDate(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
export const fmtShort = (iso) => (iso ? toDate(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—')
export const fmtStamp = (ms) => new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/* ---------- responsive ---------- */
export const useNarrow = (breakpoint) => {
    const limit = breakpoint || 900
    const [narrow, setNarrow] = useState(typeof window !== 'undefined' ? window.innerWidth < limit : false)
    useEffect(() => {
        const onResize = () => setNarrow(window.innerWidth < limit)
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [limit])
    return narrow
}

/* ---------- file export (real download, no backend) ---------- */
export const downloadCsv = (filename, rows) => {
    const esc = (v) => {
        const s = v === null || v === undefined ? '' : String(v)
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
    }
    const csv = rows.map((r) => r.map(esc).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/* ---------- presentational pieces ---------- */

/** Dark-blue gradient header used by the other V3 reports. */
export const PageHero = ({ icon, title, subtitle, right }) => (
    <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563EB 100%)', padding: '1.6rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 12, padding: '0.75rem', display: 'flex', color: 'white', fontSize: '1.75rem' }}>{icon}</div>
            <div>
                <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.375rem', margin: 0 }}>{title}</h1>
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.8rem' }}>{subtitle}</p>
            </div>
        </div>
        {right ? <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>{right}</div> : null}
    </div>
)

export const DemoBanner = ({ onReset }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E40AF', borderRadius: 10, padding: '10px 14px', fontSize: '0.82rem', marginBottom: 14, flexWrap: 'wrap' }}>
        <AiOutlineInfoCircle style={{ fontSize: '1.1rem', flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 220 }}><strong>Interactive demo.</strong> Everything here works with sample data and nothing is saved on a server. Your changes are kept while this browser tab is open, across all Field Ops pages; Reset brings the original data back.</span>
        <button type="button" onClick={onReset} style={{ ...BTN_SMALL, borderColor: '#BFDBFE', color: '#1E40AF' }}>Reset demo data</button>
    </div>
)

export const NotAllowed = ({ text }) => (
    <div style={{ marginTop: 60, textAlign: 'center', color: COLORS.slate }}>
        <AiOutlineLock style={{ fontSize: '2.5rem' }} />
        <h4 style={{ marginTop: 10, color: COLORS.navy }}>Not available for this user</h4>
        <p>{text || 'Pick another user in the demo selector at the top.'}</p>
    </div>
)

export const Loading = ({ text }) => (
    <div style={{ padding: '4rem 0', textAlign: 'center', color: COLORS.slate }}>
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <span style={{ marginLeft: 10 }}>{text || 'Loading...'}</span>
    </div>
)

export const EmptyState = ({ title, text, action }) => (
    <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: '2.2rem 1rem', color: COLORS.slate }}>
        <AiOutlineInbox style={{ fontSize: '2rem', color: COLORS.muted }} />
        <div style={{ fontWeight: 700, color: COLORS.navy, marginTop: 6 }}>{title}</div>
        {text ? <div style={{ fontSize: '0.85rem', marginTop: 2 }}>{text}</div> : null}
        {action ? <div style={{ marginTop: 12 }}>{action}</div> : null}
    </div></div>
)

const TONES = {
    green: { bg: '#DCFCE7', fg: '#166534' }, blue: { bg: '#DBEAFE', fg: '#1E40AF' },
    amber: { bg: '#FEF3C7', fg: '#92400E' }, red: { bg: '#FEE2E2', fg: '#991B1B' },
    purple: { bg: '#EDE9FE', fg: '#5B21B6' }, gray: { bg: '#F1F5F9', fg: '#475569' },
}
export const Chip = ({ tone, children }) => {
    const t = TONES[tone] || TONES.gray
    return <span style={{ display: 'inline-block', background: t.bg, color: t.fg, borderRadius: 999, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{children}</span>
}

export const barColor = (p) => (p >= 100 ? COLORS.green : p >= 70 ? COLORS.blue : p >= 40 ? COLORS.amber : COLORS.red)
/** Progress bar with the % printed next to it. Colour follows how close the value is to the target. */
export const ProgressBar = ({ value, max, height, color, hideLabel }) => {
    const p = pct(value, max)
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}>
            <div style={{ flex: 1, background: COLORS.soft, borderRadius: 999, height: height || 8, overflow: 'hidden' }}>
                <div style={{ width: Math.min(p, 100) + '%', height: '100%', background: color || barColor(p), borderRadius: 999, transition: 'width .3s' }}></div>
            </div>
            {hideLabel ? null : <span style={{ fontSize: '0.75rem', fontWeight: 700, color: COLORS.navy, width: 38, textAlign: 'right' }}>{p}%</span>}
        </div>
    )
}

export const Kpi = ({ label, value, sub, status }) => (
    <div className={'metric-card status-' + (status || 'primary')} style={{ height: '100%' }}>
        <div className="metric-label">{label}</div>
        <div className="metric-value">{value}</div>
        {sub ? <div style={{ fontSize: '0.75rem', color: COLORS.slate, marginTop: 2 }}>{sub}</div> : null}
    </div>
)

/** < Label > navigator used for the month and the day. */
export const Stepper = ({ label, onPrev, onNext, onToday, canPrev, canNext, dark }) => {
    const fg = dark ? COLORS.navy : 'white'
    const bg = dark ? '#FFFFFF' : 'rgba(255,255,255,0.12)'
    const arrow = (disabled) => ({ ...BTN_SMALL, background: 'transparent', border: 'none', color: fg, fontSize: '1rem', opacity: disabled ? 0.3 : 1 })
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: bg, border: dark ? '1px solid ' + COLORS.line : 'none', borderRadius: 10, padding: 4 }}>
            <button type="button" onClick={onPrev} disabled={canPrev === false} style={arrow(canPrev === false)} aria-label="Previous">&#8249;</button>
            <span style={{ color: fg, fontWeight: 700, fontSize: '0.85rem', minWidth: 130, textAlign: 'center' }}>{label}</span>
            <button type="button" onClick={onNext} disabled={canNext === false} style={arrow(canNext === false)} aria-label="Next">&#8250;</button>
            {onToday ? <button type="button" onClick={onToday} style={{ ...BTN_SMALL, marginLeft: 4 }}>Today</button> : null}
        </div>
    )
}

/** Horizontally scrollable pill tabs. items: [{ key, label, badge? }] */
export const Tabs = ({ items, active, onChange }) => (
    <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 14, WebkitOverflowScrolling: 'touch' }}>
        {items.map((t) => {
            const on = t.key === active
            return (
                <button key={t.key} type="button" onClick={() => onChange(t.key)}
                    style={{ ...BTN_SECONDARY, whiteSpace: 'nowrap', background: on ? COLORS.navy : '#FFFFFF', color: on ? '#FFFFFF' : COLORS.navy, borderColor: on ? COLORS.navy : COLORS.line, padding: '0.45rem 1rem' }}>
                    {t.label}
                    {t.badge ? <span style={{ marginLeft: 6, background: on ? 'rgba(255,255,255,0.2)' : COLORS.soft, borderRadius: 999, padding: '0 7px', fontSize: '0.7rem' }}>{t.badge}</span> : null}
                </button>
            )
        })}
    </div>
)

/** Small labelled form field. */
export const Field = ({ label, hint, error, children }) => (
    <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: COLORS.slate, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</label>
        {children}
        {error ? <div style={{ color: COLORS.red, fontSize: '0.75rem', marginTop: 3 }}>{error}</div> : (hint ? <div style={{ color: COLORS.muted, fontSize: '0.72rem', marginTop: 3 }}>{hint}</div> : null)}
    </div>
)

/** Right-hand slide-over panel (record details). */
export const Drawer = ({ show, title, subtitle, onClose, children, footer }) => {
    useEffect(() => {
        if (!show) return undefined
        const onKey = (e) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [show, onClose])
    if (!show) return null
    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1080 }}>
            <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }}></div>
            <div role="dialog" aria-label={title} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(520px, 100%)', background: '#FFFFFF', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 30px rgba(15,23,42,0.25)' }}>
                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)', color: 'white', padding: '1.1rem 1.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{title}</div>
                        {subtitle ? <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>{subtitle}</div> : null}
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.3rem', cursor: 'pointer' }}><AiOutlineClose /></button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.2rem 1.4rem', background: COLORS.bg }}>{children}</div>
                {footer ? <div style={{ borderTop: '1px solid ' + COLORS.line, padding: '0.8rem 1.4rem', display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>{footer}</div> : null}
            </div>
        </div>
    )
}

/** Toast stack. toasts: [{ id, text, tone }] */
export const Toasts = ({ toasts, onDismiss }) => (
    <div className="fo-toasts" style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 2000, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 'calc(100% - 32px)' }}>
        {toasts.map((t) => {
            const tone = t.tone === 'error' ? { bg: '#FEF2F2', bd: '#FECACA', fg: '#991B1B' } : t.tone === 'warn' ? { bg: '#FFFBEB', bd: '#FDE68A', fg: '#92400E' } : { bg: '#ECFDF5', bd: '#A7F3D0', fg: '#065F46' }
            return (
                <div key={t.id} onClick={() => onDismiss(t.id)} style={{ background: tone.bg, border: '1px solid ' + tone.bd, color: tone.fg, borderRadius: 10, padding: '10px 14px', fontSize: '0.83rem', boxShadow: '0 6px 20px rgba(15,23,42,0.15)', cursor: 'pointer', minWidth: 240 }}>{t.text}</div>
            )
        })}
    </div>
)

/** Tiny async simulation so Save buttons show the same "saving" state the real endpoints will have. */
export const useSaving = () => {
    const [busy, setBusy] = useState(false)
    const alive = useRef(true)
    useEffect(() => () => { alive.current = false }, [])
    const run = (fn, ms) => {
        setBusy(true)
        setTimeout(() => {
            if (!alive.current) return
            fn() // may close (unmount) the modal that owns this hook
            if (alive.current) setBusy(false)
        }, ms || 450)
    }
    return [busy, run]
}
